import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Grid,
  Paper,
  Typography,
  makeStyles,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Box,
  Container,
  Tabs,
  Tab,
  Chip,
  Snackbar,
  Divider,
  TextField,
  Collapse,
  IconButton,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Drawer,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch
} from '@material-ui/core';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';

// ─── Model column definitions ───────────────────────────────────
const MODEL_COLUMNS = {
  workTickets: {
    label: 'Work Tickets',
    modelKey: 'workTickets',
    columns: [
      { field: 'WorkTicketNumber', label: 'WT#', width: 100 },
      { field: 'WorkTicketStatusName', label: 'Status', width: 100 },
      { field: 'PropertyName', label: 'Property', width: 200 },
      { field: 'CrewLeaderName', label: 'Crew Leader', width: 150 },
      { field: 'ServiceName', label: 'Service', width: 150 },
      { field: 'ScheduledStartDate', label: 'Scheduled', width: 120, format: 'date' },
      { field: 'HoursEst', label: 'Est Hrs', width: 80 },
      { field: 'HoursAct', label: 'Act Hrs', width: 80 }
    ],
    defaultSort: 'ScheduledStartDate'
  },
  workTicketVisits: {
    label: 'Work Ticket Visits',
    modelKey: 'workTicketVisits',
    columns: [
      { field: 'WorkTicketNumber', label: 'WT#', width: 100 },
      { field: 'RouteName', label: 'Route', width: 150 },
      { field: 'ScheduledDate', label: 'Scheduled', width: 120, format: 'date' },
      { field: 'SequenceNum', label: 'Seq', width: 60 },
      { field: 'Hours', label: 'Hours', width: 80 },
      { field: 'RouteID', label: 'Route ID', width: 90 },
      { field: 'WorkTicketID', label: 'WT ID', width: 90 }
    ],
    defaultSort: 'ScheduledDate'
  },
  issues: {
    label: 'Aspire Issues',
    modelKey: 'issues',
    columns: [
      { field: 'IssueNumber', label: 'Issue#', width: 80 },
      { field: 'Subject', label: 'Subject', width: 200 },
      { field: 'IssueStatusName', label: 'Status', width: 100 },
      { field: 'IssueTypeName', label: 'Category', width: 120 },
      { field: 'IssuePriorityName', label: 'Priority', width: 90 },
      { field: 'PropertyName', label: 'Property', width: 200 },
      { field: 'AssignedToContactName', label: 'Assigned To', width: 150 },
      { field: 'CreatedDateTime', label: 'Created', width: 120, format: 'date' },
      { field: 'ResolvedDateTime', label: 'Completed', width: 120, format: 'date' }
    ],
    defaultSort: 'CreatedDateTime'
  },
  receipts: {
    label: 'Purchase Receipts',
    modelKey: 'receipts',
    columns: [
      { field: 'ReceiptNumber', label: 'Receipt#', width: 90 },
      { field: 'VendorName', label: 'Vendor', width: 180 },
      { field: 'ReceiptStatusName', label: 'Status', width: 100 },
      { field: 'ReceiptTotalCost', label: 'Total', width: 90, format: 'currency' },
      { field: 'WorkTicketNumber', label: 'WT#', width: 90 },
      { field: 'CreatedBy', label: 'Created By', width: 150 },
      { field: 'CreatedDate', label: 'Created', width: 120, format: 'date' },
      { field: 'ReceiptHasAttachment', label: 'Attach', width: 70, format: 'bool' }
    ],
    defaultSort: 'CreatedDate'
  },
  routes: {
    label: 'Routes',
    modelKey: 'routes',
    columns: [
      { field: 'RouteID', label: 'Route ID', width: 90 },
      { field: 'RouteName', label: 'Name', width: 200 },
      { field: 'CrewLeaderName', label: 'Crew Leader', width: 150 },
      { field: 'DivisionName', label: 'Division', width: 120 },
      { field: 'RouteSize', label: 'Size', width: 60 },
      { field: 'IsActive', label: 'Active', width: 70, format: 'bool' }
    ],
    defaultSort: 'RouteName'
  },
  routingProperties: {
    label: 'Routing Properties',
    modelKey: 'routingProperties',
    columns: [
      { field: 'propertyId', label: 'Prop ID', width: 80 },
      { field: 'name', label: 'Name', width: 200 },
      { field: 'address1', label: 'Address', width: 200 },
      { field: 'futureTicketCount', label: 'Future Visits', width: 100 },
      { field: 'netProfit', label: 'Net Profit', width: 100, format: 'currency' },
      { field: 'profitMargin', label: 'Margin', width: 80, format: 'pct' }
    ],
    defaultSort: 'name'
  },
  dailyRoutes: {
    label: 'Daily Routes',
    modelKey: 'dailyRoutes',
    columns: [
      { field: 'date', label: 'Date', width: 110, format: 'date' },
      { field: 'crewLeaderName', label: 'Crew Leader', width: 150 },
      { field: 'totalStops', label: 'Stops', width: 60 },
      { field: 'totalServiceTime', label: 'Svc Time', width: 80 },
      { field: 'totalDriveTime', label: 'Drive Time', width: 80 },
      { field: 'totalMiles', label: 'Miles', width: 70 },
      { field: 'isOptimized', label: 'Optimized', width: 80, format: 'bool' }
    ],
    defaultSort: 'date'
  },
  companies: {
    label: 'Companies',
    modelKey: 'companies',
    columns: [
      { field: 'CompanyID', label: 'ID', width: 70 },
      { field: 'CompanyName', label: 'Name', width: 250 },
      { field: 'Active', label: 'Active', width: 70, format: 'bool' },
      { field: 'matchStatus', label: 'Match', width: 100 },
      { field: 'lastSyncedAt', label: 'Last Synced', width: 130, format: 'date' }
    ],
    defaultSort: 'CompanyName'
  },
  contacts: {
    label: 'Contacts',
    modelKey: 'contacts',
    columns: [
      { field: 'ContactID', label: 'ID', width: 70 },
      { field: 'ContactName', label: 'Name', width: 200 },
      { field: 'CompanyName', label: 'Company', width: 150 },
      { field: 'RoleName', label: 'Role', width: 120 },
      { field: 'Email', label: 'Email', width: 180 },
      { field: 'IsActive', label: 'Active', width: 70, format: 'bool' }
    ],
    defaultSort: 'ContactName'
  },
  properties: {
    label: 'Properties',
    modelKey: 'properties',
    columns: [
      { field: 'PropertyID', label: 'ID', width: 70 },
      { field: 'PropertyName', label: 'Name', width: 200 },
      { field: 'PropertyAddressLine1', label: 'Address', width: 200 },
      { field: 'PropertyAddressCity', label: 'City', width: 120 },
      { field: 'matchStatus', label: 'Match', width: 100 }
    ],
    defaultSort: 'PropertyName'
  },
  catalogItems: {
    label: 'Catalog Items',
    modelKey: 'catalogItems',
    columns: [
      { field: 'CatalogItemID', label: 'ID', width: 70 },
      { field: 'ItemName', label: 'Name', width: 250 },
      { field: 'ItemCode', label: 'Code', width: 100 },
      { field: 'CategoryName', label: 'Category', width: 150 },
      { field: 'Price', label: 'Price', width: 80, format: 'currency' },
      { field: 'IsActive', label: 'Active', width: 70, format: 'bool' }
    ],
    defaultSort: 'ItemName'
  }
};

// ─── Styles ─────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    background: 'linear-gradient(45deg, #516e76 30%, #accacd 90%)',
    color: 'white',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)'
  },
  headerTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  statsContainer: {
    marginBottom: theme.spacing(3)
  },
  statCard: {
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8]
    }
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main
  },
  syncSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: 'white'
  },
  actionButton: {
    margin: theme.spacing(1),
    minWidth: 180
  },
  lockBanner: {
    marginBottom: theme.spacing(2)
  },
  lastSynced: {
    fontSize: '0.7rem',
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(0.5)
  },
  resultSection: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    backgroundColor: '#f9f9f9',
    maxHeight: 400,
    overflow: 'auto'
  },
  chipRow: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1)
  },
  // Modal styles
  modalContent: {
    display: 'flex',
    flexDirection: 'row',
    height: '75vh',
    minHeight: 500,
    position: 'relative'
  },
  modalTableSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'flex 0.3s ease'
  },
  modalTableSideWithDetail: {
    flex: '0 0 55%'
  },
  modalSearchBar: {
    padding: theme.spacing(1, 2),
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  modalTableWrap: {
    flex: 1,
    overflow: 'auto'
  },
  tableRowClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  tableRowSelected: {
    backgroundColor: theme.palette.action.selected + ' !important'
  },
  detailPanel: {
    flex: '0 0 45%',
    borderLeft: `1px solid ${theme.palette.divider}`,
    overflow: 'auto',
    padding: theme.spacing(2),
    backgroundColor: '#fafafa'
  },
  detailField: {
    marginBottom: theme.spacing(1.5)
  },
  detailLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  detailValue: {
    fontSize: '0.9rem',
    wordBreak: 'break-all'
  }
}));

// ─── Helpers ────────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDuration(ms) {
  if (!ms) return '';
  if (ms < 1000) return `${ms}ms`;
  const sec = (ms / 1000).toFixed(1);
  if (sec < 60) return `${sec}s`;
  const min = (ms / 60000).toFixed(1);
  return `${min}m`;
}

function healthColor(status) {
  switch (status) {
    case 'healthy': return '#2e7d32';
    case 'degraded': return '#ed6c02';
    case 'unhealthy': return '#d32f2f';
    case 'disabled': return '#607d8b';
    default: return '#757575';
  }
}

function formatCellValue(value, format) {
  if (value === null || value === undefined) return '—';
  switch (format) {
    case 'date':
      return new Date(value).toLocaleDateString();
    case 'bool':
      return value ? 'Yes' : 'No';
    case 'currency':
      return typeof value === 'number' ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : value;
    case 'pct':
      return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : value;
    default:
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
}

// ─── Detail Panel Sub-component ─────────────────────────────────
const DetailPanel = ({ doc, onClose, classes }) => {
  if (!doc) return null;

  // Sort keys: IDs first, then alpha, _id/timestamps last
  const entries = Object.entries(doc).filter(([k]) => k !== '__v');
  const idKeys = entries.filter(([k]) => k.endsWith('ID') || k === 'propertyId' || k === '_id');
  const metaKeys = entries.filter(([k]) => k === 'lastSyncedAt' || k === 'createdAt' || k === 'updatedAt' || k === 'created_at' || k === 'updated_at');
  const rest = entries.filter(([k]) => !idKeys.find(([ik]) => ik === k) && !metaKeys.find(([mk]) => mk === k));

  const orderedEntries = [...idKeys, ...rest, ...metaKeys];

  return (
    <div className={classes.detailPanel}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" style={{ fontSize: '1rem' }}>Document Detail</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Divider style={{ marginBottom: 12 }} />
      {orderedEntries.map(([key, value]) => {
        let display;
        if (value === null || value === undefined) {
          display = <span style={{ color: '#999' }}>null</span>;
        } else if (typeof value === 'boolean') {
          display = value ? <Chip size="small" label="true" style={{ backgroundColor: '#4caf50', color: 'white' }} /> : <Chip size="small" label="false" variant="outlined" />;
        } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time') || key === 'lastSyncedAt' || key === 'createdAt' || key === 'updatedAt' || key === 'created_at' || key === 'updated_at') {
          const d = new Date(value);
          display = isNaN(d.getTime()) ? String(value) : d.toLocaleString();
        } else if (Array.isArray(value)) {
          display = (
            <Collapse in={true}>
              <pre style={{ fontSize: '0.75rem', background: '#f0f0f0', padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 200 }}>
                {JSON.stringify(value, null, 2)}
              </pre>
            </Collapse>
          );
        } else if (typeof value === 'object') {
          display = (
            <pre style={{ fontSize: '0.75rem', background: '#f0f0f0', padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 150 }}>
              {JSON.stringify(value, null, 2)}
            </pre>
          );
        } else {
          display = String(value);
        }

        return (
          <div key={key} className={classes.detailField}>
            <Typography className={classes.detailLabel}>{key}</Typography>
            <Typography className={classes.detailValue} component="div">{display}</Typography>
          </div>
        );
      })}
    </div>
  );
};

// ─── Browse Modal Sub-component ─────────────────────────────────
const BrowseModal = ({ open, onClose, modelConfig, classes }) => {
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');

  // Date range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Find the first date column for this model (used as the dateField param)
  const dateColumns = modelConfig?.columns?.filter(c => c.format === 'date') || [];
  const primaryDateCol = dateColumns[0] || null;
  const [activeDateField, setActiveDateField] = useState('');

  const effectiveSort = sortField || modelConfig?.defaultSort || 'lastSyncedAt';

  const fetchDocs = useCallback(async () => {
    if (!modelConfig) return;
    setLoadingDocs(true);
    try {
      const params = {
        search,
        page: page + 1,
        limit: rowsPerPage,
        sort: effectiveSort,
        order: sortOrder
      };
      const dateFieldToUse = activeDateField || primaryDateCol?.field;
      if (dateFieldToUse && (dateFrom || dateTo)) {
        params.dateField = dateFieldToUse;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
      }
      const { data } = await axios.get(`/api/aspire/routing/browse/${modelConfig.modelKey}`, { params });
      setDocs(data.docs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Browse fetch error:', err);
    } finally {
      setLoadingDocs(false);
    }
  }, [modelConfig, search, page, rowsPerPage, effectiveSort, sortOrder, dateFrom, dateTo, activeDateField, primaryDateCol]);

  useEffect(() => {
    if (open && modelConfig) {
      setDocs([]);
      setTotal(0);
      setPage(0);
      setSearch('');
      setSearchInput('');
      setSelectedDoc(null);
      setSortField(null);
      setSortOrder('desc');
      setDateFrom('');
      setDateTo('');
      setActiveDateField('');
    }
  }, [open, modelConfig]);

  useEffect(() => {
    if (open && modelConfig) {
      fetchDocs();
    }
  }, [open, modelConfig, fetchDocs]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const handleDateFilter = () => {
    setPage(0);
    // fetchDocs will pick up dateFrom/dateTo from state
    fetchDocs();
  };

  const clearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setActiveDateField('');
    setPage(0);
  };

  if (!modelConfig) return null;

  const sortIndicator = (field) => {
    if (sortField !== field && effectiveSort !== field) return null;
    if (sortField === field || (!sortField && effectiveSort === field)) {
      return <span style={{ fontSize: '0.7rem', marginLeft: 2 }}>{sortOrder === 'asc' ? ' \u25B2' : ' \u25BC'}</span>;
    }
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ style: { height: '85vh' } }}>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {modelConfig.label}
            <Chip label={`${total.toLocaleString()} docs`} size="small" style={{ marginLeft: 8 }} />
          </Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div className={classes.modalContent}>
          {/* Left: Table */}
          <div className={`${classes.modalTableSide} ${selectedDoc ? classes.modalTableSideWithDetail : ''}`}>
            {/* Search + Date Range Bar */}
            <div className={classes.modalSearchBar}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search..."
                style={{ flex: 1, minWidth: 120 }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                  )
                }}
              />
              <Button variant="contained" color="primary" size="small" onClick={handleSearch} disabled={loadingDocs}>
                Search
              </Button>
              {dateColumns.length > 0 && (
                <>
                  <Divider orientation="vertical" flexItem style={{ margin: '0 4px' }} />
                  {dateColumns.length > 1 && (
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      value={activeDateField || primaryDateCol?.field || ''}
                      onChange={(e) => setActiveDateField(e.target.value)}
                      style={{ minWidth: 100 }}
                      SelectProps={{ native: true }}
                    >
                      {dateColumns.map(dc => (
                        <option key={dc.field} value={dc.field}>{dc.label}</option>
                      ))}
                    </TextField>
                  )}
                  {dateColumns.length === 1 && (
                    <Typography variant="caption" color="textSecondary" style={{ whiteSpace: 'nowrap', alignSelf: 'center' }}>
                      {primaryDateCol.label}:
                    </Typography>
                  )}
                  <TextField
                    size="small"
                    variant="outlined"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{ width: 145 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
                    placeholder="From"
                  />
                  <Typography variant="caption" style={{ alignSelf: 'center' }}>–</Typography>
                  <TextField
                    size="small"
                    variant="outlined"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    style={{ width: 145 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontSize: '0.8rem', padding: '6px 8px' } }}
                    placeholder="To"
                  />
                  {(dateFrom || dateTo) && (
                    <IconButton size="small" onClick={clearDateFilter} title="Clear date filter">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </>
              )}
            </div>

            {loadingDocs && <Box textAlign="center" p={2}><CircularProgress size={24} /></Box>}

            {/* Table */}
            <div className={classes.modalTableWrap}>
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {modelConfig.columns.map((col) => (
                        <TableCell
                          key={col.field}
                          style={{ fontWeight: 600, width: col.width, minWidth: col.width, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handleSort(col.field)}
                        >
                          {col.label}{sortIndicator(col.field)}
                        </TableCell>
                      ))}
                      <TableCell style={{ width: 30 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {docs.map((doc) => (
                      <TableRow
                        key={doc._id}
                        className={`${classes.tableRowClickable} ${selectedDoc?._id === doc._id ? classes.tableRowSelected : ''}`}
                        onClick={() => setSelectedDoc(doc)}
                      >
                        {modelConfig.columns.map((col) => (
                          <TableCell key={col.field} style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: col.width }}>
                            {formatCellValue(doc[col.field], col.format)}
                          </TableCell>
                        ))}
                        <TableCell padding="none">
                          <ChevronRightIcon fontSize="small" color="action" />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loadingDocs && docs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={modelConfig.columns.length + 1} align="center" style={{ padding: 40 }}>
                          <Typography color="textSecondary">No documents found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onChangePage={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </div>

          {/* Right: Detail panel */}
          {selectedDoc && (
            <DetailPanel doc={selectedDoc} onClose={() => setSelectedDoc(null)} classes={classes} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ROUTE_PHONE_REQUIREMENTS = {
  crew_not_assigned: 'No crew assigned',
  crew_not_found: 'Crew record missing',
  aspire_route_mapping_missing: 'No active Aspire route',
  multiple_active_aspire_routes: 'Multiple active Aspire routes',
  aspire_contact_mapping_missing: 'Employee has no Aspire Contact ID',
  aspire_contact_record_missing: 'Aspire Contact not synced',
  aspire_contact_email_missing: 'Contact email missing',
  aspire_contact_pin_missing: 'Contact EmployeePin missing',
  aspire_contact_inactive: 'Aspire Contact inactive',
  aspire_contact_user_mapping_missing: 'Contact has no Aspire User ID',
  aspire_user_record_missing: 'Aspire User not synced',
  aspire_user_inactive: 'Aspire User inactive',
  dfl_user_mapping_missing: 'Employee has no DFL User',
  dfl_user_record_missing: 'DFL User record missing',
  dfl_user_aspire_mapping_mismatch: 'DFL User not matched to Aspire User'
};

const contactMatchOptionLabel = option => {
  if (!option) return '';
  const details = [
    `Contact ${option.contactId}`,
    option.employeeNumber ? `Employee ${option.employeeNumber}` : null,
    option.email,
    option.phone
  ].filter(Boolean);
  return `${option.contactName || 'Unnamed Contact'} — ${details.join(' • ')}`;
};

const ContactMatchDialog = ({ open, row, onClose, onMatched }) => {
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const initialSelection = row?.aspireContact ? {
      contactId: row.aspireContact.contactId,
      contactName: row.aspireContact.contactName,
      email: row.aspireContact.email,
      pinAvailable: row.aspireContact.pinAvailable,
      aspireUser: row.aspireUser || null,
      active: row.aspireContact.active
    } : null;
    setInputValue(contactMatchOptionLabel(initialSelection));
    setSearchValue('');
    setOptions(initialSelection ? [initialSelection] : []);
    setSelected(initialSelection);
    setError(null);
    return undefined;
  }, [open, row]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/aspire/timeclock/route-phone/admin/contacts', {
          params: { search: searchValue, limit: 50 }
        });
        if (active) {
          const contacts = response.data?.contacts || [];
          setOptions(selected && !contacts.some(option => option.contactId === selected.contactId)
            ? [selected, ...contacts]
            : contacts);
        }
      } catch (caught) {
        if (active) {
          setError(caught.response?.data?.message || caught.message || 'Unable to search Aspire Contacts');
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, searchValue, selected]);

  const saveMatch = async (aspireContactId) => {
    if (!row?.employee?.id) return;
    setSaving(true);
    setError(null);
    try {
      await axios.post(
        `/api/aspire/timeclock/route-phone/admin/employees/${row.employee.id}/aspire-identity`,
        { aspireContactId }
      );
      if (onMatched) await onMatched();
    } catch (caught) {
      setError(caught.response?.data?.message || caught.message || 'Unable to update the Aspire identity match');
    } finally {
      setSaving(false);
    }
  };

  const clearMatch = async () => {
    if (!window.confirm(`Clear the Aspire Contact and User match for ${row?.employee?.name || 'this employee'}?`)) return;
    await saveMatch(null);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Match {row?.employee?.name || 'Employee'} to Aspire</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" paragraph>
          Select the Aspire employee Contact. Its linked Aspire User will be matched to this employee&apos;s DFL User automatically.
        </Typography>
        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
        <Autocomplete
          options={options}
          value={selected}
          inputValue={inputValue}
          loading={loading}
          getOptionLabel={contactMatchOptionLabel}
          getOptionSelected={(option, value) => option.contactId === value.contactId}
          getOptionDisabled={option => Boolean(
            (option.assignedEmployee && option.assignedEmployee.id !== row?.employee?.id) ||
            !option.aspireUser ||
            option.aspireUser.active === false
          )}
          onChange={(event, value) => {
            setSelected(value);
            setInputValue(contactMatchOptionLabel(value));
          }}
          onInputChange={(event, value, reason) => {
            setInputValue(value);
            if (reason === 'input') {
              setSearchValue(value);
              if (selected && value !== contactMatchOptionLabel(selected)) {
                setSelected(null);
              }
            } else if (reason === 'clear') {
              setSearchValue('');
              setSelected(null);
            }
          }}
          renderOption={option => (
            <Box>
              <Typography variant="body2">{contactMatchOptionLabel(option)}</Typography>
              <Typography variant="caption" color="textSecondary">
                Aspire User {option.aspireUser?.userId || 'missing'} • PIN {option.pinAvailable ? 'available' : 'missing'}
                {option.assignedEmployee ? ` • Assigned to ${option.assignedEmployee.name}` : ''}
              </Typography>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              autoFocus
              label="Search Aspire employee Contacts"
              placeholder="Name, email, phone, employee number, Contact ID, or User ID"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
        {selected && (
          <Alert severity="success" style={{ marginTop: 16 }}>
            Selected: <strong>{selected.contactName || 'Unnamed Contact'}</strong>
            {' '}— Contact {selected.contactId} → Aspire User {selected.aspireUser?.userId || 'missing'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {row?.aspireContact && (
          <Button onClick={clearMatch} color="secondary" disabled={saving}>
            Clear Match
          </Button>
        )}
        <Box flexGrow={1} />
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={() => saveMatch(selected?.contactId)}
          color="primary"
          variant="contained"
          disabled={!selected?.contactId || saving}
        >
          {saving ? 'Saving…' : selected?.contactId ? `Save Contact ${selected.contactId}` : 'Save Match'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const aspireRouteOptionLabel = option => {
  if (!option) return '';
  const details = [
    `Route ${option.routeId}`,
    option.branchId ? `Branch ${option.branchId}` : null,
    option.divisionName,
    option.crewLeaderName
  ].filter(Boolean);
  return `${option.routeName || 'Unnamed Route'} — ${details.join(' • ')}`;
};

const RouteMatchDialog = ({ open, row, onClose, onMatched }) => {
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const initialSelection = row?.aspireRoute ? {
      ...row.aspireRoute,
      assignedCrew: row.crew || null
    } : null;
    setInputValue(aspireRouteOptionLabel(initialSelection));
    setSearchValue('');
    setOptions(initialSelection ? [initialSelection] : []);
    setSelected(initialSelection);
    setError(null);
    return undefined;
  }, [open, row]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/aspire/timeclock/route-phone/admin/routes', {
          params: { search: searchValue, limit: 75 }
        });
        if (active) {
          const routes = response.data?.routes || [];
          setOptions(selected && !routes.some(option => option.routeId === selected.routeId)
            ? [selected, ...routes]
            : routes);
        }
      } catch (caught) {
        if (active) {
          setError(caught.response?.data?.message || caught.message || 'Unable to search Aspire routes');
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, searchValue, selected]);

  const saveMatch = async routeId => {
    if (!row?.crew?.id) return;
    setSaving(true);
    setError(null);
    try {
      await axios.post(
        `/api/aspire/timeclock/route-phone/admin/crews/${row.crew.id}/aspire-route`,
        { routeId }
      );
      if (onMatched) await onMatched();
    } catch (caught) {
      setError(caught.response?.data?.message || caught.message || 'Unable to update the Aspire route match');
    } finally {
      setSaving(false);
    }
  };

  const clearMatch = async () => {
    if (!window.confirm(`Clear the Aspire route match for ${row?.crew?.name || 'this crew'}?`)) return;
    await saveMatch(null);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Match {row?.crew?.name || 'Crew'} to an Aspire Route</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" paragraph>
          Select the active Aspire route assigned to this DFL crew. Routes already assigned to another crew cannot be selected.
        </Typography>
        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
        <Autocomplete
          options={options}
          value={selected}
          inputValue={inputValue}
          loading={loading}
          getOptionLabel={aspireRouteOptionLabel}
          getOptionSelected={(option, value) => option.routeId === value.routeId}
          getOptionDisabled={option => Boolean(
            option.assignedCrew && option.assignedCrew.id !== row?.crew?.id
          )}
          onChange={(event, value) => {
            setSelected(value);
            setInputValue(aspireRouteOptionLabel(value));
          }}
          onInputChange={(event, value, reason) => {
            setInputValue(value);
            if (reason === 'input') {
              setSearchValue(value);
              if (selected && value !== aspireRouteOptionLabel(selected)) {
                setSelected(null);
              }
            } else if (reason === 'clear') {
              setSearchValue('');
              setSelected(null);
            }
          }}
          renderOption={option => (
            <Box>
              <Typography variant="body2">{aspireRouteOptionLabel(option)}</Typography>
              <Typography variant="caption" color="textSecondary">
                {option.assignedCrew
                  ? `Currently matched to ${option.assignedCrew.name || 'another crew'}`
                  : 'Available'}
              </Typography>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              autoFocus
              label="Search active Aspire routes"
              placeholder="Route name, ID, division, or crew leader"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
        {selected && (
          <Alert severity="success" style={{ marginTop: 16 }}>
            Selected: <strong>{selected.routeName || 'Unnamed Route'}</strong> — Route {selected.routeId}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {row?.aspireRoute && (
          <Button onClick={clearMatch} color="secondary" disabled={saving}>
            Clear Match
          </Button>
        )}
        <Box flexGrow={1} />
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={() => saveMatch(selected?.routeId)}
          color="primary"
          variant="contained"
          disabled={!selected?.routeId || saving || Boolean(
            selected?.assignedCrew && selected.assignedCrew.id !== row?.crew?.id
          )}
        >
          {saving ? 'Saving…' : selected?.routeId ? `Save Route ${selected.routeId}` : 'Save Route'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RoutePhoneAdminDialog = ({ open, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mutatingProfileId, setMutatingProfileId] = useState(null);
  const [matchingRow, setMatchingRow] = useState(null);
  const [routeMatchingRow, setRouteMatchingRow] = useState(null);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const loadPreflight = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/aspire/timeclock/route-phone/admin/preflight');
      setData(response.data);
    } catch (caught) {
      setError(caught.response?.data?.message || caught.message || 'Unable to load route-phone preflight');
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    loadPreflight();
  }, [loadPreflight]);

  const releaseProfile = async (profile, mode) => {
    if (!profile?.id) return;
    const replacement = mode === 'replacement';
    const confirmed = window.confirm(
      replacement
        ? `Release ${profile.deviceName || 'this phone'} for replacement? The old physical phone will be blocked from reclaiming this route.`
        : `Reset ${profile.deviceName || 'this phone'}? The same physical phone will be allowed to reconnect and provision again.`
    );
    if (!confirmed) return;

    setMutatingProfileId(profile.id);
    setError(null);
    try {
      await axios.post(`/api/aspire/timeclock/route-phone/admin/${profile.id}/release`, {
        mode,
        reason: replacement ? 'Released from cron dashboard for replacement' : 'Reset from cron dashboard for same-phone retry'
      });
      await loadPreflight();
      if (onChanged) onChanged();
    } catch (caught) {
      setError(caught.response?.data?.message || caught.message || 'Unable to release route phone');
    } finally {
      setMutatingProfileId(null);
    }
  };

  const syncAspireUsers = async () => {
    setSyncingUsers(true);
    setError(null);
    setNotice(null);
    try {
      const response = await axios.get('/api/aspire/users/sync', {
        params: { autoMatch: true, pageSize: 200 }
      });
      const summary = response.data?.summary || {};
      setNotice(
        `Aspire Users refreshed: ${summary.eligibleUsers || 0} retained from ${summary.totalUsersFetched || 0} fetched.`
      );
      await loadPreflight();
      if (onChanged) onChanged();
    } catch (caught) {
      setError(caught.response?.data?.error || caught.message || 'Unable to sync Aspire Users');
    } finally {
      setSyncingUsers(false);
    }
  };

  const summary = data?.summary || {};
  const rows = data?.employees || [];

  const handleMatched = async () => {
    setMatchingRow(null);
    await loadPreflight();
    if (onChanged) onChanged();
  };

  const handleRouteMatched = async () => {
    setRouteMatchingRow(null);
    await loadPreflight();
    if (onChanged) onChanged();
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Aspire Route-Phone Pilot Readiness</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" paragraph>
          Employees appear here when Sync Activities with Aspire is enabled. PIN values and mobile tokens are never displayed.
        </Typography>
        {data && (
          <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 16 }}>
            <Chip size="small" label={`Opted in: ${summary.optedInEmployees || 0}`} />
            <Chip size="small" color="primary" label={`Mappings ready: ${summary.mappingsReady || 0}`} />
            <Chip size="small" label={`Active phones: ${summary.activePhoneProfiles || 0}`} />
            <Chip size="small" label={`Provisioned: ${summary.provisionedPhoneProfiles || 0}`} />
            <Chip
              size="small"
              label={`Writes: ${data.writesEnabled ? 'enabled' : 'disabled'}`}
              style={{ backgroundColor: data.writesEnabled ? '#d32f2f' : '#607d8b', color: 'white' }}
            />
          </Box>
        )}

        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
        {notice && <Alert severity="success" style={{ marginBottom: 16 }}>{notice}</Alert>}
        {loading && !data ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : data ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Crew / Aspire Route</TableCell>
                  <TableCell>Aspire Contact / User</TableCell>
                  <TableCell>Mapping</TableCell>
                  <TableCell>Route Phone</TableCell>
                  <TableCell align="right">Recovery</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const profiles = row.routePhoneProfiles || [];
                  return (
                    <TableRow key={row.employee.id}>
                      <TableCell>
                        <Typography variant="body2"><strong>{row.employee.name}</strong></Typography>
                        <Typography variant="caption" color="textSecondary">#{row.employee.number || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.crew?.name || 'No crew'}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {row.aspireRoute ? `${row.aspireRoute.routeName || 'Route'} (${row.aspireRoute.routeId})` : 'No Aspire route'}
                        </Typography>
                        {row.crew && (
                          <Box mt={1}>
                            <Button size="small" variant="outlined" onClick={() => setRouteMatchingRow(row)}>
                              {row.aspireRoute ? 'Change Route' : 'Match Route'}
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.aspireContact
                            ? row.aspireContact.contactName || `Aspire Contact ${row.aspireContact.contactId}`
                            : 'No Aspire Contact'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {row.aspireContact?.email || 'No email'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          DFL {row.dflUser?.name || 'User missing'} → Aspire User {row.aspireUser?.userId || 'missing'} • PIN {row.aspireContact?.pinAvailable ? 'available' : 'missing'}
                        </Typography>
                        <Box mt={1}>
                          <Button size="small" variant="outlined" onClick={() => setMatchingRow(row)}>
                            {row.aspireContact ? 'Change Match' : 'Match Contact'}
                          </Button>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {row.mappingReady ? (
                          <Chip size="small" label="Ready" style={{ backgroundColor: '#2e7d32', color: 'white' }} />
                        ) : row.requirements.map(requirement => (
                          <Chip
                            key={requirement}
                            size="small"
                            label={ROUTE_PHONE_REQUIREMENTS[requirement] || requirement}
                            style={{ margin: 2, backgroundColor: '#fff3e0' }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        {profiles.length === 0 ? (
                          <Typography variant="body2">Not registered</Typography>
                        ) : profiles.map(profile => (
                          <Box key={profile.id} mb={profiles.length > 1 ? 1 : 0}>
                            <Typography variant="body2">{profile.deviceName || 'Unnamed phone'}</Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {profile.registrationStatus || 'registration required'}
                              {profile.lastReleaseMode ? ` • last release: ${profile.lastReleaseMode.replace(/_/g, ' ')}` : ''}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Last seen: {timeAgo(profile.lastSeenAt)}
                            </Typography>
                          </Box>
                        ))}
                      </TableCell>
                      <TableCell align="right">
                        {profiles.map(profile => {
                          const canRelease = profile.active !== false && Boolean(profile.id);
                          return (
                            <Box key={profile.id} mb={profiles.length > 1 ? 1 : 0}>
                              <Button
                                size="small"
                                disabled={!canRelease || mutatingProfileId === profile.id}
                                onClick={() => releaseProfile(profile, 'same_phone_reset')}
                              >
                                Reset
                              </Button>
                              <Button
                                size="small"
                                color="secondary"
                                disabled={!canRelease || mutatingProfileId === profile.id}
                                onClick={() => releaseProfile(profile, 'replacement')}
                              >
                                Replace
                              </Button>
                            </Box>
                          );
                        })}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!rows.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: 32 }}>
                      No employees currently have Sync Activities with Aspire enabled.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}

        {data && (
          <>
            <Typography variant="h6" style={{ marginTop: 24, marginBottom: 8 }}>
              Recent Route-Phone Activity
            </Typography>
            <TableContainer component={Paper} variant="outlined" style={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Phone / Route</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.recentEvents || []).map(event => (
                    <TableRow key={event.id}>
                      <TableCell>{new Date(event.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{event.eventType.replace(/_/g, ' ')}</TableCell>
                      <TableCell>
                        {event.deviceName || 'Unknown phone'} / {event.aspireRouteId ? `Route ${event.aspireRouteId}` : 'Unmapped route'}
                      </TableCell>
                      <TableCell>{event.registrationStatus || '—'}</TableCell>
                      <TableCell>
                        {[
                          event.details?.stage?.replace(/_/g, ' '),
                          event.details?.attemptId ? `Attempt ${event.details.attemptId}` : null,
                          event.details?.releaseMode?.replace(/_/g, ' '),
                          event.details?.errorCode,
                          event.details?.httpStatus ? `HTTP ${event.details.httpStatus}` : null,
                          Number.isFinite(event.details?.durationMs) ? `${event.details.durationMs} ms` : null,
                          event.details?.version ? `v${event.details.version}` : null
                        ].filter(Boolean).join(' • ') || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!data.recentEvents?.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No route-phone activity recorded yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={syncAspireUsers}
          disabled={loading || syncingUsers}
          startIcon={syncingUsers ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          Sync Aspire Users
        </Button>
        <Button onClick={loadPreflight} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}>
          Refresh
        </Button>
        <Button onClick={onClose} color="primary">Close</Button>
      </DialogActions>
    </Dialog>
    <ContactMatchDialog
      open={Boolean(matchingRow)}
      row={matchingRow}
      onClose={() => setMatchingRow(null)}
      onMatched={handleMatched}
    />
    <RouteMatchDialog
      open={Boolean(routeMatchingRow)}
      row={routeMatchingRow}
      onClose={() => setRouteMatchingRow(null)}
      onMatched={handleRouteMatched}
    />
    </>
  );
};

const YardTimePolicyDialog = ({ open, onClose, onChanged }) => {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewCurrent, setPreviewCurrent] = useState(false);
  const [error, setError] = useState(null);

  const loadPolicy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/aspire/timeclock/route-phone/admin/policy');
      const nextState = response.data;
      setState(nextState);
      setDraft({ ...nextState.effectivePolicy.yardTime });
      setPreview(nextState.preview);
      setChangeReason('');
      setPreviewCurrent(true);
    } catch (caught) {
      setError(caught.response?.data?.message || caught.message || 'Unable to load Yard Time policy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadPolicy();
  }, [open, loadPolicy]);

  const updateDraft = (field, value) => {
    setDraft(current => ({ ...current, [field]: value }));
    setPreviewCurrent(false);
  };

  const optionValues = (key, ids) => {
    const selected = new Set((ids || []).map(String));
    return (preview?.options?.[key] || []).filter(option => selected.has(String(option.id)));
  };

  const runPreview = async () => {
    setPreviewing(true);
    setError(null);
    try {
      const response = await axios.post('/api/aspire/timeclock/route-phone/admin/policy/preview', {
        yardTime: draft
      });
      setPreview(response.data.preview);
      setPreviewCurrent(true);
    } catch (caught) {
      setError(caught.response?.data?.message || caught.message || 'Unable to preview Yard Time policy');
      setPreviewCurrent(false);
    } finally {
      setPreviewing(false);
    }
  };

  const savePolicy = async () => {
    const currentPolicy = state?.effectivePolicy?.yardTime || {};
    const broadening = (draft.enabled && !currentPolicy.enabled) ||
      (draft.scopeMode === 'all_eligible' && currentPolicy.scopeMode !== 'all_eligible') ||
      (draft.allowedEmployeeIds || []).length > (currentPolicy.allowedEmployeeIds || []).length ||
      (draft.allowedCrewIds || []).length > (currentPolicy.allowedCrewIds || []).length ||
      (draft.allowedAspireRouteIds || []).length > (currentPolicy.allowedAspireRouteIds || []).length;
    if (broadening && !window.confirm(
      `This change broadens Yard Time to ${preview?.summary?.eligibleEmployees || 0} currently ready employees. Continue?`
    )) return;

    setSaving(true);
    setError(null);
    try {
      const response = await axios.put('/api/aspire/timeclock/route-phone/admin/policy', {
        expectedVersion: state.effectivePolicy.version,
        changeReason,
        yardTime: draft
      });
      const nextState = response.data;
      setState(nextState);
      setDraft({ ...nextState.effectivePolicy.yardTime });
      setPreview(nextState.preview);
      setChangeReason('');
      setPreviewCurrent(true);
      if (onChanged) await onChanged();
    } catch (caught) {
      setError(caught.response?.data?.message || caught.message || 'Unable to save Yard Time policy');
      if (caught.response?.status === 409) await loadPolicy();
    } finally {
      setSaving(false);
    }
  };

  const gates = state?.effectivePolicy?.environmentGates || {};
  const summary = preview?.summary || {};
  const reasonEntries = Object.entries(summary.reasonCounts || {});
  const clockInReasonEntries = Object.entries(summary.clockInReasonCounts || {});

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Yard Time Policy</DialogTitle>
      <DialogContent dividers>
        {loading && <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>}
        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}
        {!loading && draft && (
          <>
            <Alert
              severity={state.effectivePolicy.effectiveEnabled ? 'success' : 'warning'}
              style={{ marginBottom: 16 }}
            >
              Database settings can only narrow behavior. Deployment kill switches always win.
              {' '}Effective status: <strong>{state.effectivePolicy.effectiveEnabled ? 'enabled' : 'disabled'}</strong>.
            </Alert>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 20 }}>
              <Chip size="small" label={`Clock writes: ${gates.mobileClockWrites ? 'on' : 'off'}`} color={gates.mobileClockWrites ? 'primary' : 'default'} />
              <Chip size="small" label={`Work-ticket writes: ${gates.mobileWorkTicketWrites ? 'on' : 'off'}`} color={gates.mobileWorkTicketWrites ? 'primary' : 'default'} />
              <Chip size="small" label={`Yard writes: ${gates.yardTimeWrites ? 'on' : 'off'}`} color={gates.yardTimeWrites ? 'primary' : 'default'} />
              <Chip size="small" variant="outlined" label={`Policy v${state.effectivePolicy.version} · ${state.effectivePolicy.source.replace(/_/g, ' ')}`} />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={<Switch checked={draft.enabled} onChange={event => updateDraft('enabled', event.target.checked)} color="primary" />}
                  label="Create new Yard Time starts"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Clock-in behavior</InputLabel>
                  <Select
                    value={draft.clockInMode}
                    onChange={event => updateDraft('clockInMode', event.target.value)}
                    label="Clock-in behavior"
                  >
                    <MenuItem value="every_clock_in">Every distinct clock-in</MenuItem>
                    <MenuItem value="first_daily_clock_in">First eligible clock-in each day</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Aspire Yard Work Ticket ID"
                  value={draft.workTicketId}
                  onChange={event => updateDraft('workTicketId', Number(event.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={draft.locationRequirement === 'inside_hmi_yard'}
                      onChange={event => updateDraft('locationRequirement', event.target.checked ? 'inside_hmi_yard' : 'none')}
                      color="primary"
                    />
                  )}
                  label="Require clock-in inside HMI Yard"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={draft.returnToYardMode === 'detected_inside_yard'}
                      onChange={event => updateDraft('returnToYardMode', event.target.checked ? 'detected_inside_yard' : 'disabled')}
                      color="primary"
                    />
                  )}
                  label="Restart on detected Yard return"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Pilot scope</InputLabel>
                  <Select
                    value={draft.scopeMode}
                    onChange={event => updateDraft('scopeMode', event.target.value)}
                    label="Pilot scope"
                  >
                    <MenuItem value="all_eligible">All eligible employees</MenuItem>
                    <MenuItem value="allowlist">Employee, crew, or route allowlist</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {draft.scopeMode === 'allowlist' && (
              <Grid container spacing={2} style={{ marginTop: 4 }}>
                {[
                  ['employees', 'allowedEmployeeIds', 'Allowed employees'],
                  ['crews', 'allowedCrewIds', 'Allowed crews'],
                  ['routes', 'allowedAspireRouteIds', 'Allowed Aspire routes']
                ].map(([optionKey, field, label]) => (
                  <Grid item xs={12} md={4} key={field}>
                    <Autocomplete
                      multiple
                      options={preview?.options?.[optionKey] || []}
                      value={optionValues(optionKey, draft[field])}
                      getOptionLabel={option => option.label || String(option.id)}
                      getOptionSelected={(option, value) => String(option.id) === String(value.id)}
                      onChange={(event, values) => updateDraft(field, values.map(value => value.id))}
                      renderInput={params => <TextField {...params} variant="outlined" label={label} />}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12, marginTop: 20 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={runPreview}
                disabled={previewing || saving}
                startIcon={previewing ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                Preview Impact
              </Button>
              {!previewCurrent && <Chip size="small" label="Preview required after changes" style={{ backgroundColor: '#fff3e0' }} />}
              {preview?.ticket
                ? <Chip size="small" label={`Verified ticket ${preview.ticket.workTicketNumber}`} style={{ backgroundColor: '#e8f5e9' }} />
                : <Chip size="small" label="Yard Work Ticket is not synced" style={{ backgroundColor: '#ffebee' }} />}
            </Box>

            {preview && (
              <Paper variant="outlined" style={{ padding: 16, marginTop: 16 }}>
                <Typography variant="subtitle1">Impact preview</Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8, margin: '8px 0 12px' }}>
                  <Chip size="small" label={`${summary.eligibleEmployees || 0}/${summary.optedInEmployees || 0} employees ready`} />
                  <Chip size="small" label={`${summary.sameDayVisitRoutes || 0} routes have today's Yard visit`} />
                  <Chip size="small" label={`Today's clock-ins: ${summary.todayClockIns || 0}`} />
                  <Chip size="small" label={`Would start: ${summary.todayClockInsThatWouldStart || 0}`} style={{ backgroundColor: '#e8f5e9' }} />
                  <Chip size="small" label={`Would skip: ${summary.todayClockInsThatWouldSkip || 0}`} style={{ backgroundColor: '#fff3e0' }} />
                </Box>
                {(reasonEntries.length > 0 || clockInReasonEntries.length > 0) && (
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Readiness blockers: {reasonEntries.map(([reason, count]) => `${reason.replace(/_/g, ' ')} (${count})`).join(' · ') || 'none'}
                    <br />Today&apos;s skip reasons: {clockInReasonEntries.map(([reason, count]) => `${reason.replace(/_/g, ' ')} (${count})`).join(' · ') || 'none'}
                  </Typography>
                )}
                <TableContainer style={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Crew / route</TableCell>
                        <TableCell>Today</TableCell>
                        <TableCell>Readiness</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(preview.rows || []).map(row => (
                        <TableRow key={row.employeeId}>
                          <TableCell>{row.employeeName}</TableCell>
                          <TableCell>{row.crewName || 'No crew'}{row.aspireRouteId ? ` · Route ${row.aspireRouteId}` : ''}</TableCell>
                          <TableCell>{row.todayClockIns || 0} clock-in(s) · {row.todayClockInsThatWouldStart || 0} start(s)</TableCell>
                          <TableCell>{row.eligible ? 'Ready' : row.reasons.map(reason => reason.replace(/_/g, ' ')).join(', ')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <TextField
              fullWidth
              required
              variant="outlined"
              label="Change reason"
              value={changeReason}
              onChange={event => setChangeReason(event.target.value)}
              helperText="Required for the audit trail (5–500 characters)."
              style={{ marginTop: 20 }}
            />

            <Box mt={3}>
              <Typography variant="subtitle1">Recent policy changes</Typography>
              {(state.audit || []).length ? (state.audit || []).map(event => (
                <Typography key={event.id} variant="body2" color="textSecondary">
                  v{event.version} · {new Date(event.createdAt).toLocaleString()} · {event.actor?.name || 'System'} — {event.changeReason}
                </Typography>
              )) : <Typography variant="body2" color="textSecondary">No saved policy changes yet.</Typography>}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={loadPolicy} disabled={loading || saving}>Reload</Button>
        <Box flexGrow={1} />
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={savePolicy}
          disabled={saving || !previewCurrent || changeReason.trim().length < 5 || !draft}
        >
          {saving ? 'Saving…' : 'Save Audited Policy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────
const CronSyncDashboard = () => {
  const classes = useStyles();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [runningAction, setRunningAction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastResult, setLastResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  // Custom sync fields
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [fullBackfill, setFullBackfill] = useState(false);
  const [receiptWindowDays, setReceiptWindowDays] = useState('365');

  // Browse modal
  const [browseModel, setBrowseModel] = useState(null);
  const [showRoutePhoneAdmin, setShowRoutePhoneAdmin] = useState(false);
  const [showYardTimePolicy, setShowYardTimePolicy] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/aspire/routing/cron-status');
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch cron status:', err);
      setSnackbar({ open: true, message: 'Failed to load status', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const triggerAction = async (action, params) => {
    if (lastResult?._retryAction === action && !params?.force) {
      params = { ...params, force: true };
    }
    setRunningAction(action);
    try {
      const { data } = await axios.post('/api/aspire/routing/trigger-sync', { action, params });
      setLastResult(data);
      setShowResult(true);

      if (data.skipped) {
        if (data.reason === 'another_instance_running' && !params?.force) {
          setSnackbar({
            open: true,
            message: `Lock held — click again to force (lock: ${data.lockKey || 'unknown'})`,
            severity: 'warning'
          });
          setLastResult({ ...data, _retryAction: action, _retryParams: params });
        } else {
          setSnackbar({
            open: true,
            message: data.reason === 'local_dev_blocked'
              ? 'Sync blocked on local dev'
              : 'Skipped: ' + (data.reason || 'unknown'),
            severity: 'warning'
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: `${action} completed in ${formatDuration(data.duration)}`,
          severity: 'success'
        });
      }
      fetchStatus();
    } catch (err) {
      console.error(`Sync action ${action} failed:`, err);
      setSnackbar({
        open: true,
        message: `${action} failed: ${err.response?.data?.error || err.message}`,
        severity: 'error'
      });
    } finally {
      setRunningAction(null);
    }
  };

  const isRunning = runningAction !== null;
  const timeclockHealth = status?.timeclock;
  const readSyncHealth = timeclockHealth?.supportedReadSync;
  const routePhoneWrites = timeclockHealth?.routePhoneWrites;
  const yardTimeHealth = routePhoneWrites?.yardTime;
  const yardTimePolicy = yardTimeHealth?.policy?.yardTime;
  const yardTimeQueue = yardTimeHealth?.queue;
  const aspireSource = status?.aspire;

  // Map stat card keys to MODEL_COLUMNS keys for click-to-browse
  const statCards = status ? [
    {
      label: 'Work Tickets',
      value: status.workTickets?.total || 0,
      lastSynced: status.workTickets?.lastSyncedAt,
      browseKey: 'workTickets',
      chips: [
        { label: `Open: ${status.workTickets?.open || 0}`, color: '#ff9800' },
        { label: `Scheduled: ${status.workTickets?.scheduled || 0}`, color: '#2196f3' },
        { label: `Complete: ${status.workTickets?.complete || 0}`, color: '#4caf50' }
      ]
    },
    {
      label: 'Visits',
      value: status.workTicketVisits?.total || 0,
      lastSynced: status.workTicketVisits?.lastSyncedAt,
      browseKey: 'workTicketVisits',
      subtitle: status.workTicketVisits?.oldestDate && status.workTicketVisits?.newestDate
        ? `${new Date(status.workTicketVisits.oldestDate).toLocaleDateString()} → ${new Date(status.workTicketVisits.newestDate).toLocaleDateString()}`
        : null,
      chips: [
        { label: `Future: ${status.workTicketVisits?.future || 0}`, color: '#2196f3' }
      ]
    },
    {
      label: 'Routes (Active)',
      value: status.routes?.active || 0,
      lastSynced: status.routes?.lastSyncedAt,
      browseKey: 'routes'
    },
    {
      label: 'Routing Properties',
      value: status.routingProperties?.totalProperties || 0,
      lastSynced: status.routingProperties?.lastSyncedAt,
      browseKey: 'routingProperties',
      chips: status.routingProperties?.problemProperties
        ? [{ label: `Problems: ${status.routingProperties.problemProperties}`, color: '#f44336' }]
        : []
    },
    {
      label: 'Daily Routes',
      value: status.dailyRoutes?.total || 0,
      browseKey: 'dailyRoutes',
      subtitle: status.dailyRoutes?.lastCalcDate
        ? `Last calc: ${new Date(status.dailyRoutes.lastCalcDate).toLocaleDateString()}`
        : null
    },
    {
      label: 'Companies',
      value: status.companies?.total || 0,
      lastSynced: status.companies?.lastSyncedAt,
      browseKey: 'companies'
    },
    {
      label: 'Contacts',
      value: status.contacts?.total || 0,
      lastSynced: status.contacts?.lastSyncedAt,
      browseKey: 'contacts',
      chips: [
        { label: `Stale: ${status.contacts?.stale || 0}`, color: status.contacts?.stale ? '#ff9800' : '#4caf50' },
        { label: `Branches: ${(status.contacts?.branches || []).join(', ') || '—'}`, color: '#607d8b' }
      ]
    },
    {
      label: 'Properties',
      value: status.properties?.total || 0,
      lastSynced: status.properties?.lastSyncedAt,
      browseKey: 'properties'
    },
    {
      label: 'Catalog Items',
      value: status.catalogItems?.total || 0,
      lastSynced: status.catalogItems?.lastSyncedAt,
      browseKey: 'catalogItems'
    },
    {
      label: 'Issues',
      value: status.issues?.total || 0,
      lastSynced: status.issues?.lastSyncedAt,
      browseKey: 'issues',
      chips: [
        { label: `Open: ${status.issues?.open || 0}`, color: '#ff9800' },
        { label: `Completed: ${status.issues?.completed || 0}`, color: '#4caf50' }
      ]
    },
    {
      label: 'Purchase Receipts',
      value: status.receipts?.total || 0,
      lastSynced: status.receipts?.lastSyncedAt,
      browseKey: 'receipts',
      chips: [
        { label: `Linked: ${status.receipts?.linked || 0}`, color: '#4caf50' },
        { label: `On WT: ${status.receipts?.withWorkTicket || 0}`, color: '#607d8b' },
        { label: `Attachments: ${status.receipts?.withAttachment || 0}`, color: '#2196f3' }
      ]
    }
  ] : [];

  const quickActions = [
    { action: 'daily-sync', label: 'Run Daily Sync', description: 'Full pipeline: routes, companies, contacts, properties, catalog, stale tickets, visits, rollup' },
    { action: 'sync-users', label: 'Sync Aspire Users', description: 'Refresh all Aspire Users, retain branches 4/5/9, and rerun Contact/User matching' },
    { action: 'hourly-sync', label: 'Run Hourly Sync', description: "Today's work tickets, visits, clock times, work ticket times" },
    { action: 'timeclock-read-sync', label: 'Sync Timeclock Status', description: 'Read ClockTimes and WorkTicketTimes from the supported Aspire API; does not write punches' },
    { action: 'sync-opportunities', label: 'Sync Won Opportunities + Services', description: 'Fetch won opportunities plus local contract cadence and per-visit pricing' },
    { action: 'issues-sync', label: 'Sync Aspire Issues', description: 'Branch-scoped sweep of Aspire Issues (Activities) into the local mirror, with legacy-issue linking' },
    { action: 'receipts-sync', label: 'Sync Purchase Receipts', description: '90-day public-API pull of Aspire purchase receipts (branches 4/5/9) with vendor names and line items, then ownership linking for mobile Purchases' },
    { action: 'sync-visits-30day', label: 'Sync Visits 30-Day', description: 'Fetch next 30 days of visit data' },
    { action: 'sync-visits-90day', label: 'Sync Visits 90-Day', description: 'Fetch next 90 days of visit data' },
    { action: 'sync-visits-full-year', label: 'Sync Visits Full Year', description: 'Fetch remainder of current year' },
    { action: 'rebuild-future-visits', label: 'Rebuild Future Visits', description: 'Rollup visit data to RoutingProperty.futureVisits' },
    { action: 'backfill-visit-ticket-metadata', label: 'Backfill Visit Metadata', description: 'Hydrate local WorkTicketVisits with opportunity, service, status, property, and route assignment from WorkTickets' },
    { action: 'recalculate-routing-properties', label: 'Recalculate Routing Property Metrics', description: 'Rebuild per-property estimated, average, and median on-site hours from Aspire work tickets' }
  ];

  return (
    <Container maxWidth="xl" className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="h4" className={classes.headerTitle}>
              Aspire Cron Sync Dashboard
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.9 }}>
              Monitor sync status and trigger manual syncs — click any card to browse data
            </Typography>
          </div>
          <Button
            variant="outlined"
            style={{ color: 'white', borderColor: 'white' }}
            startIcon={loading ? <CircularProgress size={18} style={{ color: 'white' }} /> : <RefreshIcon />}
            onClick={fetchStatus}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {aspireSource && (
        <Alert
          severity={aspireSource.environment === 'production' ? 'warning' : 'info'}
          style={{ marginBottom: 24 }}
        >
          <strong>Aspire {aspireSource.environment || 'unknown'}:</strong>{' '}
          {aspireSource.host || 'API host is not configured'} · Contact branches{' '}
          {(aspireSource.contactBranches || []).join(', ') || 'not configured'}
        </Alert>
      )}

      {/* Active Locks Banner */}
      {status?.activeLocks?.length > 0 && (
        <Alert severity="warning" icon={<LockIcon />} className={classes.lockBanner}>
          <strong>Active Locks ({status.activeLocks.length}):</strong>{' '}
          {status.activeLocks.map((lock, i) => (
            <span key={i}>
              {lock._id} (owner: {lock.owner}, expires: {timeAgo(lock.expiresAt)})
              {i < status.activeLocks.length - 1 ? ' | ' : ''}
            </span>
          ))}
        </Alert>
      )}

      {/* Read-only timeclock health and operator entry point */}
      <Paper className={classes.syncSection}>
        <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" style={{ gap: 16 }}>
          <Box>
            <Typography variant="h6">Aspire Timeclock</Typography>
            <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8, marginTop: 8 }}>
              <Chip
                size="small"
                label={`Read sync: ${readSyncHealth?.assessment?.status || 'unknown'}`}
                style={{ backgroundColor: healthColor(readSyncHealth?.assessment?.status), color: 'white' }}
              />
              <Chip
                size="small"
                label={`Route-phone writes: ${routePhoneWrites?.assessment?.status || 'unknown'}`}
                style={{ backgroundColor: healthColor(routePhoneWrites?.assessment?.status), color: 'white' }}
              />
              <Chip
                size="small"
                label={`Route-phone readiness: ${routePhoneWrites?.readinessAssessment?.status || 'unknown'} (${routePhoneWrites?.counts?.provisioned || 0}/${routePhoneWrites?.configuredProfiles || 0})`}
                style={{ backgroundColor: healthColor(routePhoneWrites?.readinessAssessment?.status), color: 'white' }}
              />
              <Chip
                size="small"
                label={`Clock queue: ${(routePhoneWrites?.commands?.pending || 0) + (routePhoneWrites?.commands?.claimed || 0)} pending · ${routePhoneWrites?.commands?.retrying || 0} retrying · ${routePhoneWrites?.commands?.failed || 0} failed`}
                style={{ backgroundColor: healthColor(routePhoneWrites?.assessment?.status), color: 'white' }}
              />
              <Chip
                size="small"
                label={yardTimePolicy
                  ? `Yard: ${yardTimeHealth?.policy?.effectiveEnabled ? yardTimePolicy.clockInMode.replace(/_/g, ' ') : 'disabled'} · WT ${yardTimePolicy.workTicketId}`
                  : 'Yard: policy unavailable'}
                style={{ backgroundColor: healthColor(yardTimeHealth?.assessment?.status), color: 'white' }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Yard gates: ${yardTimeHealth?.policy?.environmentGates?.mobileClockWrites && yardTimeHealth?.policy?.environmentGates?.mobileWorkTicketWrites && yardTimeHealth?.policy?.environmentGates?.yardTimeWrites ? 'on' : 'blocked'}`}
              />
              <Chip
                size="small"
                label={`Yard queue: ${(yardTimeQueue?.commands?.pending || 0) + (yardTimeQueue?.commands?.claimed || 0)} pending · ${yardTimeQueue?.commands?.retrying || 0} retrying · ${yardTimeQueue?.commands?.failed || 0} failed · ${yardTimeQueue?.activeIntervals || 0} active`}
                style={{ backgroundColor: healthColor(yardTimeHealth?.assessment?.status), color: 'white' }}
              />
              {Object.keys(yardTimeQueue?.skipReasons || {}).length > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Yard skips: ${Object.entries(yardTimeQueue.skipReasons).map(([reason, count]) => `${reason.replace(/_/g, ' ')} ${count}`).join(' · ')}`}
                />
              )}
            </Box>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
              Last successful read: {timeAgo(readSyncHealth?.lastSuccessfulAt)}. Yard queue oldest pending: {timeAgo(yardTimeQueue?.oldestPendingAt)}; last success: {timeAgo(yardTimeQueue?.lastSuccessfulAt)}. Supported reads run on the server; undocumented writes will originate from the assigned route phone.
            </Typography>
          </Box>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowRoutePhoneAdmin(true)}
            >
              Pilot Mappings & Phones
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowYardTimePolicy(true)}
            >
              Yard Time Policy
            </Button>
            <Button
              variant="outlined"
              color="primary"
              href="/aspire-timeclock"
            >
              View Employee Clock Status
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                runningAction === 'timeclock-read-sync'
                  ? <CircularProgress size={20} style={{ color: 'white' }} />
                  : <RefreshIcon />
              }
              onClick={() => triggerAction('timeclock-read-sync')}
              disabled={isRunning}
            >
              Sync Read Data Now
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Stat Cards — clickable to browse */}
      <Grid container spacing={2} className={classes.statsContainer}>
        {statCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
            <Card
              className={classes.statCard}
              onClick={() => card.browseKey && setBrowseModel(MODEL_COLUMNS[card.browseKey])}
            >
              <CardContent>
                <Typography variant="body2" color="textSecondary">{card.label}</Typography>
                <Typography className={classes.statNumber}>{card.value.toLocaleString()}</Typography>
                {card.subtitle && (
                  <Typography variant="caption" color="textSecondary">{card.subtitle}</Typography>
                )}
                {card.chips && card.chips.length > 0 && (
                  <div className={classes.chipRow}>
                    {card.chips.map((chip, ci) => (
                      <Chip
                        key={ci}
                        label={chip.label}
                        size="small"
                        style={{ backgroundColor: chip.color, color: 'white', fontSize: '0.7rem' }}
                      />
                    ))}
                  </div>
                )}
                {card.lastSynced && (
                  <Typography className={classes.lastSynced}>
                    Last synced: {timeAgo(card.lastSynced)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Sync Actions Panel */}
      <Paper className={classes.syncSection}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Quick Actions" />
          <Tab label="Custom Sync" />
        </Tabs>

        <Divider style={{ marginBottom: 16 }} />

        {activeTab === 0 && (
          <Box>
            <Grid container spacing={2}>
              {quickActions.map(({ action, label, description }) => (
                <Grid item xs={12} sm={6} md={4} key={action}>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.actionButton}
                    fullWidth
                    startIcon={
                      runningAction === action
                        ? <CircularProgress size={20} style={{ color: 'white' }} />
                        : <PlayArrowIcon />
                    }
                    onClick={() => triggerAction(action)}
                    disabled={isRunning}
                  >
                    {label}
                  </Button>
                  <Typography variant="caption" color="textSecondary" style={{ display: 'block', paddingLeft: 8 }}>
                    {description}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Custom Date Range Sync</Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fullBackfill}
                      onChange={(e) => setFullBackfill(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: fullBackfill ? 600 : 400 }}>
                        Full Backfill
                      </Typography>
                      <Typography variant="caption" color="textSecondary" style={{ display: 'block', lineHeight: 1.2 }}>
                        {fullBackfill
                          ? 'Tickets + visits + WorkTicketTimes + real-hour enrichment + DailyRoutes'
                          : 'Visits only — does not refresh historical crew hours'}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  color={fullBackfill ? 'secondary' : 'primary'}
                  fullWidth
                  startIcon={
                    (runningAction === 'sync-visits-custom' || runningAction === 'backfill-sync')
                      ? <CircularProgress size={20} style={{ color: 'white' }} />
                      : <ScheduleIcon />
                  }
                  onClick={() => {
                    if (!customStartDate || !customEndDate) {
                      setSnackbar({ open: true, message: 'Start and end dates are required', severity: 'warning' });
                      return;
                    }
                    const action = fullBackfill ? 'backfill-sync' : 'sync-visits-custom';
                    triggerAction(action, {
                      startDate: `${customStartDate}T00:00:00Z`,
                      endDate: `${customEndDate}T23:59:59Z`
                    });
                  }}
                  disabled={isRunning || !customStartDate || !customEndDate}
                >
                  {fullBackfill ? 'Run Full Backfill' : 'Sync Visits'}
                </Button>
              </Grid>
            </Grid>

            <Divider style={{ margin: '24px 0' }} />

            <Typography variant="h6" gutterBottom>Purchase Receipts Backfill</Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Window (days)"
                  type="number"
                  fullWidth
                  value={receiptWindowDays}
                  onChange={(e) => setReceiptWindowDays(e.target.value)}
                  helperText="Public-API pull filtered to receipts created inside this window (branches 4/5/9). Hourly cron covers 90 days; use 365 for the first-run backfill."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  startIcon={
                    runningAction === 'receipts-sync'
                      ? <CircularProgress size={20} style={{ color: 'white' }} />
                      : <ScheduleIcon />
                  }
                  onClick={() => {
                    const days = parseInt(receiptWindowDays, 10);
                    if (!days || isNaN(days) || days < 1) {
                      setSnackbar({ open: true, message: 'Enter a valid window in days', severity: 'warning' });
                      return;
                    }
                    triggerAction('receipts-sync', { windowDays: days });
                  }}
                  disabled={isRunning || !receiptWindowDays}
                >
                  Backfill Receipts
                </Button>
              </Grid>
            </Grid>

            <Divider style={{ margin: '24px 0' }} />

            <Typography variant="h6" gutterBottom>Resync Single Property</Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Aspire Property ID"
                  type="number"
                  fullWidth
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  placeholder="e.g. 12345"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  startIcon={
                    runningAction === 'resync-property'
                      ? <CircularProgress size={20} style={{ color: 'white' }} />
                      : <RefreshIcon />
                  }
                  onClick={() => {
                    const pid = parseInt(propertyId, 10);
                    if (!pid || isNaN(pid)) {
                      setSnackbar({ open: true, message: 'Enter a valid numeric property ID', severity: 'warning' });
                      return;
                    }
                    triggerAction('resync-property', { propertyId: pid });
                  }}
                  disabled={isRunning || !propertyId}
                >
                  Resync Property
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Last Sync Results */}
      {lastResult && (
        <Paper className={classes.syncSection}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Last Sync Result
              {lastResult.action && (
                <Chip
                  label={lastResult.action}
                  size="small"
                  color={lastResult.success ? 'primary' : 'secondary'}
                  style={{ marginLeft: 8 }}
                />
              )}
              {lastResult.duration && (
                <Chip
                  label={formatDuration(lastResult.duration)}
                  size="small"
                  variant="outlined"
                  style={{ marginLeft: 4 }}
                />
              )}
            </Typography>
            <IconButton onClick={() => setShowResult(!showResult)} size="small">
              {showResult ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showResult}>
            <Paper className={classes.resultSection} variant="outlined">
              <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(lastResult, null, 2)}
              </pre>
            </Paper>
          </Collapse>
        </Paper>
      )}

      {/* Browse Modal */}
      <BrowseModal
        open={!!browseModel}
        onClose={() => setBrowseModel(null)}
        modelConfig={browseModel}
        classes={classes}
      />

      <RoutePhoneAdminDialog
        open={showRoutePhoneAdmin}
        onClose={() => setShowRoutePhoneAdmin(false)}
        onChanged={fetchStatus}
      />

      <YardTimePolicyDialog
        open={showYardTimePolicy}
        onClose={() => setShowYardTimePolicy(false)}
        onChanged={fetchStatus}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  authenticated: state.authenticated
});

export default connect(mapStateToProps)(CronSyncDashboard);
